import * as Tooltip from "@radix-ui/react-tooltip";
import {
  Ban,
  BriefcaseBusiness,
  CircleCheck,
  Clock3,
  FolderOpen,
  IndianRupee,
  Info,
  Shield,
  ShieldCheck,
} from "lucide-react";
import React from "react";

import { formatIndianCompactNumber } from "../../../../../src/lib/utils";

interface ICasesAnalyticsOverviewProps {
  casesOpened?: number;
  fraudAmount?: number;
  declaredFraud?: number;
  declaredNoFraud?: number;
  casesInProgress?: number;
  casesOpen?: number;
  casesDeactivated?: number;
  casesClosed?: number;
  isLoading?: boolean;
}

const CasesAnalyticsOverview: React.FC<ICasesAnalyticsOverviewProps> = (
  props,
) => {
  const titleIconStyle: React.CSSProperties = {
    width: 12.666666984558105,
    height: 15.335567474365234,
    opacity: 1,
  };
  const titleIconStrokeWidth = 1.33;

  const casesOverviewMetrics = [
    {
      title: "All Cases",
      value: props.casesOpened ?? "-",
      titleIcon: (
        <BriefcaseBusiness
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#2563EB"
        />
      ),
      color: "bg-blue-500",
    },
    {
      title: "Fraud Amount",
      value:
        typeof props.fraudAmount === "number"
          ? formatIndianCompactNumber(props.fraudAmount, {
              includeCurrency: true,
            })
          : "-",
      titleIcon: (
        <IndianRupee
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#DC2626"
        />
      ),
      definition: "SUM of exposure",
      color: "bg-red-500",
    },
    {
      title: "Declared Fraud",
      value: props.declaredFraud ?? "-",
      titleIcon: (
        <Shield
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#DC2626"
        />
      ),
      definition:
        "Count of cases with FIC classification value is not null and NO FRAUD",
      color: "bg-red-500",
    },
    {
      title: "Declared No Fraud",
      value: props.declaredNoFraud ?? "-",
      titleIcon: (
        <ShieldCheck
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#16A34A"
        />
      ),
      definition: "Count of cases with FIC classification value is NO FRAUD",
      color: "bg-emerald-600",
    },
    {
      title: "In Progress",
      value: props.casesInProgress ?? "-",
      titleIcon: (
        <Clock3
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#D97706"
        />
      ),
      color: "bg-amber-500",
    },
    {
      title: "Open Cases",
      value: props.casesOpen ?? "-",
      titleIcon: (
        <FolderOpen
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#2563EB"
        />
      ),
      color: "bg-blue-500",
    },
    {
      title: "Deactivated",
      value: props.casesDeactivated ?? "-",
      titleIcon: (
        <Ban
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#6B7280"
        />
      ),
      color: "bg-gray-500",
    },
    {
      title: "Closed Cases",
      value: props.casesClosed ?? "-",
      titleIcon: (
        <CircleCheck
          style={titleIconStyle}
          strokeWidth={titleIconStrokeWidth}
          color="#16A34A"
        />
      ),
      color: "bg-emerald-600",
    },
  ];

  return (
    <Tooltip.Provider delayDuration={200}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {casesOverviewMetrics.map((card, index) => (
          <div
            key={index}
            className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 ${
              props.isLoading ? "animate-pulse" : ""
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1 mb-2">
                  <span className="inline-flex items-center justify-center">
                    {card.titleIcon}
                  </span>
                  <span className="font-sans font-medium text-xs leading-4 tracking-normal text-gray-500 whitespace-nowrap">
                    {card.title}
                  </span>
                  {card.definition ? (
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-sm text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                          aria-label={`Info: ${card.title}`}
                        >
                          <Info className="w-3 h-3" />
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          side="top"
                          sideOffset={8}
                          className="z-[9999] max-w-[320px] rounded-md bg-gray-900/85 px-3 py-2 text-xs leading-5 text-white shadow-xl backdrop-blur-sm"
                        >
                          {card.definition}
                          <Tooltip.Arrow className="fill-gray-900/85" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  ) : null}
                </div>
                {props.isLoading ? (
                  <div className="h-7 sm:h-8 w-28 rounded bg-gray-200" />
                ) : (
                  <h3 className="font-sans font-medium text-xl sm:text-2xl leading-7 sm:leading-8 tracking-normal whitespace-nowrap">
                    {card.value}
                  </h3>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Tooltip.Provider>
  );
};

export default CasesAnalyticsOverview;
